import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import navigationItems from '~/common/constants/navigation';
import gsap from 'gsap';
import { Flex } from '~/components/wrappers/Flex';
import { Routes } from '~/common/constants/routes';
import { colors } from '~/theme/colors';
import { StyledNavigationItem } from '../header';
import { Badge } from '~/components/badge';
import { useCartSSR } from '~/recoil/useCartSSR';
import { useNavigation } from '~/common/hooks/useNavigation';
import { Image } from '~/components/wrappers/Image';
import XIcon from 'public/assets/icons/x.svg';
import BagOutline from 'public/assets/icons/bag_outline.svg';
import { NavigationItem } from '~/components/navigations/NavigationItems';
import { fontFamilies } from '~/theme/typography';
import { regionInfoState, regionModalOpenState } from '~/recoil/useRegion';
import CountryFlag from '~/components/CountryFlag';
import { useRegionsEnabled } from '~/common/hooks/useRegionsEnabled';

const MobileMenu = ({
  isOpen,
  activeTab,
  toggleMobileNavigation,
}: {
  isOpen?: boolean;
  activeTab?: string;
  toggleMobileNavigation: (val: boolean) => void;
}) => {
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { goToHome, goTo } = useNavigation();
  const [{ count }] = useCartSSR();
  const [isMounted, setIsMounted] = useState(false);
  const setRegionModalOpen = useSetRecoilState(regionModalOpenState);
  const regionsEnabled = useRegionsEnabled();
  const { countryCode } = useRecoilValue(regionInfoState);

  const handleOpenRegionModal = () => {
    toggleMobileNavigation(false);
    setRegionModalOpen(true);
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const menu = mobileMenuRef.current;
    if (!menu) return;

    // `inert` removes the subtree from tab order and the accessibility tree
    // while the menu is visually translated offscreen, so keyboard and screen
    // reader users can't reach hidden links.
    menu.inert = !isOpen;

    if (isOpen) {
      document.body.style.overflow = 'hidden';

      gsap.to(menu, {
        duration: 0.4,
        y: 0,
        ease: 'power2.out',
      });
    } else {
      document.body.style.overflow = 'auto';

      gsap.to(menu, {
        duration: 0.3,
        y: '-100%',
        ease: 'power2.in',
      });
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isMounted) return null;

  return createPortal(
    <MobileMenuContainer ref={mobileMenuRef} aria-hidden={!isOpen}>
      <MenuTopRow>
        <StyledButton onClick={() => toggleMobileNavigation(false)}>
          <Image
            src={XIcon}
            alt="Close Broken Planet navigation menu"
            width={40}
            height={40}
          />
        </StyledButton>
        <LogoWrapper onClick={goToHome}>
          <Logo
            src="/assets/icons/header-logo.svg"
            alt="Logo of broken planet"
            loading="eager"
            width={208}
            height={24}
          />
        </LogoWrapper>
        <RightIcons>
          {regionsEnabled && (
            <RegionIconButton
              type="button"
              aria-label="Select store"
              onClick={handleOpenRegionModal}
            >
              <CountryFlag code={countryCode} />
            </RegionIconButton>
          )}
          <Badge
            count={count}
            $backgroundColor={colors.greyDisabled2}
            $transformCount={0}
          >
            <StyledNavigationItem
              to={Routes.Cart}
              onClick={() => goTo(Routes.Cart)}
              aria-label="Bag"
            >
              <Image src={BagOutline} alt="" width={24} height={24} />
            </StyledNavigationItem>
          </Badge>
        </RightIcons>
      </MenuTopRow>
      <Flex
        $width="100%"
        $direction="column"
        $alignItems="center"
        $minHeight="22.5rem"
        $rowGap="1.5rem"
      >
        {navigationItems.map((item) => (
          <NavigationItem
            key={item.title}
            $active={activeTab === item.location}
            displayTitle={item.displayTitle}
            location={item.location}
          />
        ))}
      </Flex>
    </MobileMenuContainer>,
    document.body,
  );
};

export const Logo = styled.img`
  cursor: pointer;

  @media ${({ theme }) => theme.breakpoints.md} {
    width: 8.75rem;
  }
`;

const BagText = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.0075rem;
  text-transform: uppercase;
`;

const LogoWrapper = styled.button`
  background-color: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  height: 4.5rem;

  @media ${({ theme }) => theme.breakpoints.md} {
    height: 2.875rem;
  }
`;

const MobileMenuContainer = styled.div`
  position: fixed;
  z-index: ${({ theme }) => theme.zIndex.mobileMenu};
  background-color: ${({ theme }) => theme.colors.colorMain};
  bottom: 0;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  align-items: center;
  gap: 2.5rem;
  flex-direction: column;
  display: flex;
  transform: translateY(-100%);
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.5);
  overflow-y: hidden;
  /* Offset keeps the top row level with the header row under the rolling header. */
  padding: calc(1.5rem + var(--rolling-header-offset, 0px)) 1.5rem 2.5rem;

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: calc(1rem + var(--rolling-header-offset, 0px)) 1rem 2.5rem;
  }

  @media (max-height: 23.4375rem) {
    display: block;
    overflow-y: scroll;
  }
`;

// Mirrors the header's row so the logo does not jump horizontally when the
// menu opens. See StyledHeader in ~/layout/components/header, which explains why
// the grid is symmetric and why a fourth child would break it.
const MenuTopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  width: 100%;
`;

const RightIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-self: end;
`;

const RegionIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  > img {
    width: 24px;
    height: 24px;
  }
`;

const StyledButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  justify-self: start;

  :disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  > img {
    max-width: 40px;
    width: 100%;
  }
`;

export default MobileMenu;
